import { css } from "@linaria/core";
import React, { useCallback, useMemo, useState } from "react";
import { uniq } from "diagnostics-lib/array";
import { ArrowRightLine } from "@ui/IconV2/react";
import Select from "@ui/Select";
import { useAvailableResources } from "../hook/useAvailableResources";
import type { BlockPlotImportT } from "../schema/block/plot";
import type { DashboardImportT } from "../schema/dashboard";
import { colors, spacings } from "@ui/Theme";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Text, { H4 } from "@ui/Text";
import t from "../../../../i18n";
import type { EncodingT } from "../schema/encoding";
import type { BlockDiagramImportT } from "../schema/block/diagram";
import { getAvailableResourceKind } from "../utils/getAvailableResourceKind";

const IdentifierMappingContainer = css`
  display: grid;
  grid-auto-rows: max-content;
  grid-template-columns: 1fr max-content 1fr;
  align-items: center;
  gap: ${spacings.s};
`;

const IdentifierMappingInfo = css`
  grid-column: 1 / -1;
`;

const IdentifierMappingTitle = css`
  grid-column: 1 / -1;
`;

const IdentifierKind = css`
  color: ${colors.carbon800};
`;

export function IdentifierMapping({
  projectId,
  dashboard,
  identifierMapping,
  registerIdentifierMapping,
}: {
  projectId: string;
  dashboard: DashboardImportT;
  identifierMapping: ReturnType<typeof useIdentifierMapping>["identifierMapping"];
  registerIdentifierMapping: ReturnType<typeof useIdentifierMapping>["registerIdentifierMapping"];
}) {
  const availableResources = useAvailableResources({ projectId });

  const availableResourceOptions = useMemo(
    () =>
      [
        availableResources.quantity,
        availableResources.fault,
        availableResources.system,
        availableResources.aggregate,
      ].flatMap(resource =>
        resource.identifiers
          .map(({ name, unit }) => ({
            name,
            unit,
            kind: resource.kind,
          }))
          .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : 0))
          .map(({ name, unit, kind }) => ({
            label: `${name} ${unit ? `(${unit})` : ""} — ${t("project.dashboard.encoding.kind", kind)}`,
            value: name,
          })),
      ),
    [availableResources],
  );

  const blocks = dashboard.blocks.filter(
    (b): b is BlockPlotImportT | BlockDiagramImportT => b.kind === "PLOT" || b.kind === "DIAGRAM",
  );

  const identifiers = blocks.flatMap(block => {
    if (block.kind === "PLOT")
      return block.config.layers.flatMap(layer =>
        Object.values(layer.encoding).map(encoding => encoding.identifier),
      );
    if (block.kind === "DIAGRAM")
      return block.config.layers.flatMap(layer =>
        layer.encodings.map(encoding => encoding.identifier),
      );
    return [];
  });

  //   const identifiers = useMemo(
  //     () =>
  //       uniq(
  //         blocks
  //           .filter(
  //             (b): b is BlockPlotImportT | BlockDiagramImportT =>
  //               b.kind === "PLOT" || b.kind === "DIAGRAM",
  //           )
  //           .flatMap(block => [
  //             ...(block.data.resources?.map(({ identifier }) => identifier) ?? []),
  //             ...(block.kind === "PLOT"
  //               ? block.config.layers
  //                   .flatMap(l => [l.encoding.x, l.encoding.y])
  //                   .map(e => e.identifier)
  //               : []),
  //           ])
  //           .filter((x): x is string => typeof x === "string"),
  //       ),
  //     [blocks],
  //   );

  const missingIdentifierKinds = useMemo(
    () =>
      Object.fromEntries(
        identifiers
          .map(identifier => [identifier, getAvailableResourceKind(availableResources, identifier)])
          .filter(([, kind]) => kind),
      ),
    [availableResources, identifiers],
  );

  const missingIdentifiers = useMemo(
    () =>
      uniq(
        identifiers.filter(identifier =>
          availableResourceOptions.every(({ value }) => value !== identifier),
        ),
      ),
    [availableResourceOptions, identifiers],
  );

  const unknownIdentifierKinds = useMemo(
    () => missingIdentifiers.filter(identifier => !missingIdentifierKinds[identifier]),
    [missingIdentifierKinds, missingIdentifiers],
  );

  const showMissingIdentifierWarning = useMemo(() => {
    const mappedIdentifierWithKind = Object.keys(identifierMapping).filter(
      identifier => missingIdentifierKinds[identifier],
    );
    return (
      Object.keys(identifierMapping).length === 0 ||
      mappedIdentifierWithKind.some(identifier => !identifierMapping[identifier])
    );
  }, [identifierMapping, missingIdentifierKinds]);

  if (missingIdentifiers.length === 0) return null;

  return (
    <div className={IdentifierMappingContainer}>
      {showMissingIdentifierWarning && (
        <AdditionnalMessage
          status="WARNING"
          text={t("project.dashboards.import.missing_identifier")}
          className={IdentifierMappingInfo}
        />
      )}
      {unknownIdentifierKinds.length > 0 && (
        <AdditionnalMessage
          status="INFO"
          text={t("project.dashboards.import.unknown_identifier_kind", unknownIdentifierKinds)}
          className={IdentifierMappingInfo}
        />
      )}
      <H4 className={IdentifierMappingTitle}>
        {t("project.dashboards.import.identifier_mapping_title")}
      </H4>
      {missingIdentifiers.map(identifier => (
        <React.Fragment key={identifier}>
          <Text type="body1SemiBold" as="span">
            {identifier}
            <br />
            <Text type="body2" as="span" className={IdentifierKind}>
              {missingIdentifierKinds[identifier]
                ? t("project.dashboard.encoding.kind", missingIdentifierKinds[identifier])
                : t("project.dashboard.encoding.unknown")}
            </Text>
          </Text>
          <ArrowRightLine size="s" color={colors.carbon800} />
          <Select
            prompt={t("project.dashboards.import.select_identifier")}
            // disabled={unknownIdentifierKinds.includes(identifier)}
            filtered
            options={availableResourceOptions
              .filter(o =>
                missingIdentifierKinds[identifier]
                  ? o.label.includes(
                      t("project.dashboard.encoding.kind", missingIdentifierKinds[identifier]),
                    )
                  : true,
              )
              .map(o => ({
                label: missingIdentifierKinds[identifier]
                  ? o.label.replace(
                      `— ${t("project.dashboard.encoding.kind", missingIdentifierKinds[identifier])}`,
                      "",
                    )
                  : o.label,
                value: o.value,
              }))}
            value={identifierMapping[identifier]}
            onChange={e => registerIdentifierMapping(identifier, e.target.value)}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

export function useIdentifierMapping() {
  const [mapping, setMapping] = useState<{ [key: string]: string }>({});
  const registerIdentifierMapping = useCallback(
    (oldIdentifier: string, newIdentifier: string) =>
      setMapping(s => ({ ...s, [oldIdentifier]: newIdentifier })),
    [],
  );
  const applyIdentifierMapping = useCallback(
    (dashboard: DashboardImportT) => {
      const blocks = structuredClone(dashboard).blocks.map(block => {
        if (block.kind === "PLOT") {
          const mappedLayers = block.config.layers.map(layer => ({
            ...layer,
            encoding: {
              x: {
                ...layer.encoding.x,
                identifier: mapping[layer.encoding.x.identifier] ?? layer.encoding.x.identifier,
              },
              y: {
                ...layer.encoding.y,
                identifier: mapping[layer.encoding.y.identifier] ?? layer.encoding.y.identifier,
              },
              ...(layer.encoding.color
                ? {
                    color: {
                      ...layer.encoding.color,
                      identifier:
                        mapping[layer.encoding.color.identifier] ?? layer.encoding.color.identifier,
                    },
                  }
                : {}),
            },
          }));
          block.config.layers = mappedLayers;
        }

        if (block.kind === "DIAGRAM") {
          const mappedLayers = block.config.layers.map(layer => ({
            ...layer,
            encodings: layer.encodings
              .map((encoding, i) => {
                const identifier = layer.encodings[i]?.identifier;
                if (!identifier) return null;
                return {
                  ...encoding,
                  identifier: mapping[identifier] ?? identifier,
                };
              })
              .filter((encoding): encoding is NonNullable<EncodingT> => Boolean(encoding)),
          }));
          block.config.layers = mappedLayers;
        }
        return block;
      });

      return {
        ...dashboard,
        blocks,
      };
    },
    [mapping],
  );

  return { identifierMapping: mapping, registerIdentifierMapping, applyIdentifierMapping };
}
